import type { DataSource, SourceMetadata } from "../types/imports";
import { tokenStore } from "./tokenStore";

const API_BASE_URL =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://apicashboard.com"
    : ""; // In dev, requests go to same origin and are proxied by webpack to localhost:8000

function getAuthHeader(): Record<string, string> {
  const token = tokenStore.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface ApiError {
  error: string;
  status: number;
}

class ApiRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const body: ApiError = await response.json();
      message = body.error || message;
    } catch {
      // ignore parse errors
    }
    throw new ApiRequestError(message, response.status);
  }

  return response.json();
}

// ---------- Sources ----------

interface ListSourcesResponse {
  sources: DataSource[];
}

export async function fetchSources(
  orgId: string,
  search?: string,
): Promise<DataSource[]> {
  const params = new URLSearchParams({ org_id: orgId });
  if (search) {
    params.set("search", search);
  }
  const data = await apiFetch<ListSourcesResponse>(
    `/api/excel/sources/?${params.toString()}`,
  );
  // Normalize Metabase integer IDs to strings for the frontend
  return data.sources.map((s) => ({
    ...s,
    id: String(s.id),
  }));
}

// ---------- Source Metadata ----------

interface SourceMetadataResponse {
  source: SourceMetadata;
}

export async function fetchSourceMetadata(
  orgId: string,
  cardId: string,
): Promise<SourceMetadata> {
  const params = new URLSearchParams({ org_id: orgId });
  const data = await apiFetch<SourceMetadataResponse>(
    `/api/excel/sources/${cardId}/?${params.toString()}`,
  );
  return data.source;
}

// ---------- Query Execution ----------

export interface QueryResponse {
  columns: string[];
  rows: (string | number | null)[][];
  row_count: number;
}

export async function executeSourceQuery(
  orgId: string,
  cardId: string,
  cardName?: string,
): Promise<QueryResponse> {
  return apiFetch<QueryResponse>(`/api/excel/sources/${cardId}/query/`, {
    method: "POST",
    body: JSON.stringify({ org_id: orgId, card_name: cardName }),
  });
}

// ---------- Streaming Query (CSV) ----------

/**
 * Execute a source query and return the response body as a ReadableStream
 * of CSV bytes. Does NOT call response.json() — the caller is responsible
 * for parsing the CSV stream incrementally.
 */
export interface DateFilterParam {
  column: string;
  preset: string;
  after_month?: string;
}

export async function executeSourceQueryStream(
  orgId: string,
  cardId: string,
  cardName?: string,
  dateFilter?: DateFilterParam,
): Promise<ReadableStream<Uint8Array>> {
  const bodyObj: Record<string, unknown> = {
    org_id: orgId,
    card_name: cardName,
  };
  if (dateFilter) {
    bodyObj.date_filter = dateFilter;
  }

  const response = await fetch(
    `${API_BASE_URL}/api/excel/sources/${cardId}/query/stream/`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      body: JSON.stringify(bodyObj),
    },
  );

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const body: ApiError = await response.json();
      message = body.error || message;
    } catch {
      // ignore parse errors
    }
    throw new ApiRequestError(message, response.status);
  }

  if (!response.body) {
    throw new ApiRequestError("No response body", 0);
  }

  return response.body;
}

export { ApiRequestError };
